import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';
import ButtonSpinner from '../components/ButtonSpinner';
import { useSelector } from 'react-redux';

const AddClass = () => {
  // Move hooks to the top to satisfy ESLint react-hooks/rules-of-hooks
  const navigate = useNavigate();
  const { user: userInfo } = useSelector((state) => state.auth);
  const { data, loading: teachersLoading, error: teachersError } = useFetch(`${API_BASE_URL}/api/web/admin/add-class-page`, 'addClassPage');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    className: '',
    classSuffix: null,
    classLevel: '',
    classTeacher: null,
    classPrefect: null,
    asstClassPrefect: null,
    maxStudents: 40,
    description: '',
  });

  const teachers = data?.teachers || [];
  const students = data?.students || []; // Assuming students are included in the API response

  // Restrict access to admins
  const userRole = userInfo?.role || 'staff';
  
  if (userRole !== 'admin') {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-red-600 text-center">
          Access Denied: Only admins can create classes.
        </div>
      </div>
    );
  }

  const teacherOptions = teachers.map((t) => ({
    value: t.uuid,
    label: `${t.title} ${t.lastname} ${t.firstname} (${t.phone || 'N/A'})`,
  }));

  const studentOptions = students.map((s) => ({
    value: s.uuid,
    label: `${s.lastname} ${s.firstname} (${s.phone || 'N/A'})`,
  }));

  const classSuffixOptions = [
    { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' },
    { value: 'D', label: 'D' }, { value: 'E', label: 'E' }, { value: 'F', label: 'F' },
    { value: 'G', label: 'G' }, { value: 'H', label: 'H' }, { value: 'I', label: 'I' },
    { value: 'J', label: 'J' }, { value: 'K', label: 'K' }, { value: 'L', label: 'L' },
    { value: 'M', label: 'M' }, { value: 'N', label: 'N' }, { value: 'O', label: 'O' },
    { value: 'P', label: 'P' }, { value: 'Q', label: 'Q' }, { value: 'R', label: 'R' },
    { value: 'S', label: 'S' }, { value: 'T', label: 'T' }, { value: 'U', label: 'U' },
    { value: 'V', label: 'V' }, { value: 'W', label: 'W' }, { value: 'X', label: 'X' },
    { value: 'Y', label: 'Y' }, { value: 'Z', label: 'Z' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name) => (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.className || !formData.classLevel || !formData.classTeacher) {
      alert('Please fill all required fields: Class Name, Class Level, and Class Form Teacher.');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      className: formData.className,
      classSuffix: formData.classSuffix?.value,
      classLevel: formData.classLevel,
      classTeacher: formData.classTeacher?.value,
      classPrefect: formData.classPrefect?.value,
      asstClassPrefect: formData.asstClassPrefect?.value,
      maxStudents: formData.maxStudents,
      description: formData.description,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/web/admin/add-class-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        alert('Class created successfully!');
        navigate('/all-classes');
      } else {
        alert(result.message || 'Failed to create class.');
      }
    } catch (error) {
      console.error('Error creating class:', error);
      alert('Failed to create class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      backgroundColor: '#fff',
      '&:hover': {
        borderColor: '#3b82f6',
      },
      // Dark mode
      '[data-theme="dark"] &': {
        backgroundColor: '#1f2937',
        borderColor: '#4b5563',
        color: '#fff',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      '[data-theme="dark"] &': {
        color: '#fff',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : '#fff',
      color: state.isSelected ? '#fff' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#f3f4f6',
      },
      // Dark mode
      '[data-theme="dark"] &': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#1f2937',
        color: state.isSelected ? '#fff' : '#d1d5db',
        '&:hover': {
          backgroundColor: state.isSelected ? '#3b82f6' : '#374151',
        },
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      '[data-theme="dark"] &': {
        backgroundColor: '#1f2937',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: '#374151',
      '[data-theme="dark"] &': {
        color: '#fff',
      },
    }),
  };

  if (teachersLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="w-12 h-12 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-100 dark:bg-gray-900 min-h-screen">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Create New Class</h1>
            <p className="text-blue-100">Add a new class to the school</p>
          </div>
          <Link
            to="/all-classes"
            className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
          >
            <FaArrowLeft className="w-5 h-5" />
            <span>Back to Classes</span>
          </Link>
        </div>
      </div>

      {/* Error Display */}
      {teachersError && (
        <div className="text-red-600 text-center mb-4">{teachersError}</div>
      )}

      {/* Form Container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 border border-gray-200 dark:border-gray-600">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Class Name */}
            <div className="space-y-2">
              <label htmlFor="className" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="className"
                name="className"
                value={formData.className}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., JSS1"
                required
              />
            </div>

            {/* Class Suffix */}
            <div className="space-y-2">
              <label htmlFor="classSuffix" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Suffix
              </label>
              <Select
                id="classSuffix"
                name="classSuffix"
                value={formData.classSuffix}
                onChange={handleSelectChange('classSuffix')}
                options={classSuffixOptions}
                placeholder="Select Alphabet"
                isSearchable
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Class Level */}
            <div className="space-y-2">
              <label htmlFor="classLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Level <span className="text-red-500">*</span>
              </label>
              <select
                id="classLevel"
                name="classLevel"
                value={formData.classLevel}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="">Select Level</option>
                <option value="junior">Junior Secondary School</option>
                <option value="senior">Senior Secondary School</option>
              </select>
            </div>

            {/* Class Form Teacher */}
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="classTeacher" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Form Teacher <span className="text-red-500">*</span>
              </label>
              <Select
                id="classTeacher"
                name="classTeacher"
                value={formData.classTeacher}
                onChange={handleSelectChange('classTeacher')}
                options={teacherOptions}
                placeholder="Select Teacher"
                isSearchable
                isClearable
                required
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Class Prefect */}
            <div className="space-y-2">
              <label htmlFor="classPrefect" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Prefect
              </label>
              <Select
                id="classPrefect"
                name="classPrefect"
                value={formData.classPrefect}
                onChange={handleSelectChange('classPrefect')}
                options={studentOptions}
                placeholder="Select Student"
                isSearchable
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Assistant Class Prefect */}
            <div className="space-y-2">
              <label htmlFor="asstClassPrefect" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Assistant Class Prefect
              </label>
              <Select
                id="asstClassPrefect"
                name="asstClassPrefect"
                value={formData.asstClassPrefect}
                onChange={handleSelectChange('asstClassPrefect')}
                options={studentOptions}
                placeholder="Select Student"
                isSearchable
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Maximum Students */}
            <div className="space-y-2">
              <label htmlFor="maxStudents" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Maximum Students
              </label>
              <input
                type="number"
                id="maxStudents"
                name="maxStudents"
                value={formData.maxStudents}
                onChange={handleChange}
                min="1"
                max="50"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter class description"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-6">
            <Link
              to="/all-classes"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <ButtonSpinner size="small" color="white" />
                  Creating...
                </>
              ) : (
                <>
                  <FaSave />
                  Create Class
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClass;